import { useCallback, useEffect, useRef } from "react";

import { captureException } from "@sentry/react";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent } from "constants/index";
import {
  useEmbeddedDashboardSession,
  useRoleContext,
  useWriterComponentState,
} from "hooks";

import { EmbeddedDashboardSkeleton } from "./components/EmbeddedDashboardSkeleton";
import { LuzmoDashboard } from "./components/LuzmoDashboard";
import { EMBEDDED_DASHBOARD_PATHS, EmbeddedAnalyticsProvider } from "./types";

import type {
  EmbeddedDashboardName,
  EmbeddedDashboardViewerState,
} from "components/EmbeddedAnalytics/types";

import type {
  EmbeddedDashboardScreenMode,
  MeasureDimensionPickerUpdate,
} from "./components/LuzmoDashboard/utils";

interface Props {
  dashboard: EmbeddedDashboardName;
}

// A person ticking several columns in a row produces one save, not one per
// tick; the picker itself is live, so nothing waits on the write.
const VIEWER_STATE_SAVE_DELAY_MS = 800;

/**
 * Provider-agnostic embedded dashboard.
 *
 * Fetches the viewer session and hands it to the renderer named by the
 * response, so a change of analytics vendor adds a renderer here rather than
 * touching the pages. Presentation choices the renderer reports (today: which
 * columns a table shows) are saved per user so the next session starts from
 * them.
 */
export function EmbeddedDashboard(props: Props) {
  const { dashboard } = props;
  const { clientInit, loading, error } = useEmbeddedDashboardSession({
    dashboard,
  });
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const viewCaptured = useRef(false);
  // Every table's latest picker state, so a save always carries the whole
  // dashboard's choices rather than only the table that changed last.
  const pickerStateByItemRef = useRef<Record<string, unknown>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { write: saveViewerState } = useWriterComponentState({
    errorMessage: "Failed to save your column selection",
    writeConfig: {
      method: "put",
      url: `/shippers/${role?.id}/viz/embedded/${EMBEDDED_DASHBOARD_PATHS[dashboard]}/viewer-state`,
    },
    // A lost preference is not worth interrupting the dashboard for, but it is
    // worth knowing about.
    onWriteFailure: (writeError) => captureException(writeError),
  });

  const handleMeasureDimensionPickerChange = useCallback(
    (update: MeasureDimensionPickerUpdate) => {
      pickerStateByItemRef.current = {
        ...pickerStateByItemRef.current,
        [update.itemId]: update.pickerState,
      };
      posthog?.capture(ClientAppEvent.EMBEDDED_DASHBOARD_COLUMNS_CHANGED, {
        dashboard,
        itemId: update.itemId,
      });
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const body: EmbeddedDashboardViewerState = {
          measure_dimension_picker: pickerStateByItemRef.current,
        };
        saveViewerState(undefined, body);
      }, VIEWER_STATE_SAVE_DELAY_MS);
    },
    [dashboard, posthog, saveViewerState],
  );

  // The collected choices belong to one dashboard: when this component shows
  // a different one (or unmounts), a pending save for the old dashboard is
  // dropped rather than sent under the new dashboard's name, and the map
  // starts empty.
  useEffect(
    () => () => {
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      pickerStateByItemRef.current = {};
    },
    [dashboard],
  );

  useEffect(() => {
    // Once per mounted dashboard: the session refetches on focus, which would
    // otherwise report a fresh view every time the user returns to the tab.
    if (clientInit !== null && !viewCaptured.current) {
      viewCaptured.current = true;
      posthog?.capture(ClientAppEvent.EMBEDDED_DASHBOARD_VIEWED, {
        dashboard,
        provider: clientInit.provider,
      });
    }
  }, [clientInit, dashboard, posthog]);

  // Reported separately from the view above rather than folded into it: that
  // one fires as soon as the session arrives, before the provider has drawn
  // anything, so it cannot carry the layout. Keeping both also makes the gap
  // between them legible — a dashboard viewed but never rendered is the
  // silent failure the embed's `error` event does not catch.
  const handleRendered = useCallback(
    (screenMode: EmbeddedDashboardScreenMode) => {
      posthog?.capture(ClientAppEvent.EMBEDDED_DASHBOARD_RENDERED, {
        dashboard,
        screenMode,
      });
    },
    [dashboard, posthog],
  );

  if (error !== null) {
    return <ErrorTypography error={error} />;
  }

  // The same skeleton covers the renderer while the embed initializes, so the
  // whole load — session fetch included — reads as one continuous state.
  if (loading) {
    return <EmbeddedDashboardSkeleton />;
  }

  return (
    <>
      {clientInit?.provider === EmbeddedAnalyticsProvider.LUZMO && (
        // Keyed so switching dashboards remounts the renderer; same-dashboard
        // session refreshes update it in place.
        <LuzmoDashboard
          key={clientInit.payload.dashboard_id}
          payload={clientInit.payload}
          onMeasureDimensionPickerChange={handleMeasureDimensionPickerChange}
          onRendered={handleRendered}
        />
      )}
    </>
  );
}
